import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./Header.scss";

import logo from "../../assets/images/logos/techadive_logo.svg";

const Header = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const toggleRef = useRef(null);
    const headerRef = useRef(null);
    const location = useLocation();

    const closeMenu = () => setMenuOpen(false);

    // Close the menu after navigation.
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname, location.hash]);

    useEffect(() => {
        if (!menuOpen) return;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setMenuOpen(false);
                toggleRef.current?.focus();
            }
        };

        const handleOutsideClick = (event) => {
            if (!headerRef.current?.contains(event.target)) {
                setMenuOpen(false);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("pointerdown", handleOutsideClick);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("pointerdown", handleOutsideClick);
        };
    }, [menuOpen]);

    // Reset the mobile menu when switching to desktop.
    useEffect(() => {
        const desktop = window.matchMedia("(min-width: 901px)");

        const handleResize = (event) => {
            if (event.matches) setMenuOpen(false);
        };

        desktop.addEventListener("change", handleResize);

        return () => {
            desktop.removeEventListener("change", handleResize);
        };
    }, []);

    const handleSectionClick = (event, sectionId) => {
        closeMenu();

        // Preserve normal link behaviour for modified clicks.
        if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return;
        }

        if (location.pathname !== "/") return;

        const section = document.getElementById(sectionId);
        if (!section) return;

        event.preventDefault();

        const reducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        section.scrollIntoView({
            behavior: reducedMotion ? "auto" : "smooth",
            block: "start",
        });
    };

    const handleHomeClick = () => {
        closeMenu();

        if (location.pathname === "/") {
            const reducedMotion = window.matchMedia(
                "(prefers-reduced-motion: reduce)"
            ).matches;

            window.scrollTo({
                top: 0,
                behavior: reducedMotion ? "auto" : "smooth",
            });
        }
    };

    return (
        <header className="header" ref={headerRef}>
            <div className="header__inner">
                <Link
                    className="header__logo"
                    to="/"
                    aria-label="Techadive homepage"
                    onClick={handleHomeClick}
                >
                    <img src={logo} alt="Techadive" width="140" />
                </Link>

                <button
                    className={`header__menu-toggle ${
                        menuOpen ? "header__menu-toggle--active" : ""
                    }`}
                    type="button"
                    ref={toggleRef}
                    aria-label={menuOpen ? "Close navigation" : "Open navigation"}
                    aria-expanded={menuOpen}
                    aria-controls="primary-navigation"
                    onClick={() => setMenuOpen((previous) => !previous)}
                >
                    <span aria-hidden="true" />
                    <span aria-hidden="true" />
                    <span aria-hidden="true" />
                </button>

                <nav
                    className={`header__nav ${
                        menuOpen ? "header__nav--open" : ""
                    }`}
                    id="primary-navigation"
                    aria-label="Main navigation"
                    onBlur={(event) => {
                        if (!headerRef.current?.contains(event.relatedTarget)) {
                            closeMenu();
                        }
                    }}
                >
                    <ul className="header__links">
                        <li>
                            <a
                                className="header__link"
                                href="/#what-we-build"
                                onClick={(event) => handleSectionClick(event, "what-we-build")}
                            >
                                What We Build
                            </a>
                        </li>

                        <li>
                            <NavLink
                                className="header__link"
                                to="/invest-africa"
                                onClick={closeMenu}
                            >
                                Invest Africa
                            </NavLink>
                        </li>

                        <li>
                            <a
                                className="header__link"
                                href="/#ventures"
                                onClick={(event) =>
                                    handleSectionClick(event, "ventures")
                                }
                            >
                                Ventures
                            </a>
                        </li>

                        <li>
                            <NavLink
                                className="header__link"
                                to="/about"
                                onClick={closeMenu}
                            >
                                About
                            </NavLink>
                        </li>
                    </ul>

                    <Link
                        className="header__cta"
                        to="/contact"
                        onClick={closeMenu}
                    >
                        Contact Us
                        <span aria-hidden="true">↗</span>
                    </Link>
                </nav>
            </div>
        </header>
    );
};

export default Header;